import React, {
  useContext,
  useReducer,
  useState
} from 'react';

import {
  BrowserRouter,
  Navigate,
  Routes,
  Route,
  useLocation,
  useNavigate
} from 'react-router-dom';

import {
  QueryClient,
  QueryClientProvider
} from 'react-query';

import axios from 'axios';

import { ReactQueryDevtools } from 'react-query/devtools';
import { apiBase } from './constants/config';

import Admin from './components/admin';
import Dashboard from './components/consignment/Dashboard';
import Main from './components/Main';
import Registration from './components/Registration';
import ValidateAuth from './components/ValidateAuth';
import SuperAdminLogin from './components/SuperAdminLogin';
import Logout from './components/Logout';
import Invalid from './components/Invalid';
import UserRecord from './records/user';
import { useHydrateAuth } from './hooks/useAuth';
import entities, { entitiesState } from './reducers/entities';

export const AuthContext = React.createContext();
export const UserContext = React.createContext();
export const ApiContext = React.createContext();
export const EntitiesContext = React.createContext();

const queryClient = new QueryClient();

function App() {
  const [user, setUser] = useState(new UserRecord());
  const [auth, setAuth] = useState(null);
  const [state, dispatch] = useReducer(entities, entitiesState);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthContext.Provider value={{ auth, setAuth }}>
        <ApiContext.Provider value={axios.create({
            baseURL: apiBase()
          })}>
          <EntitiesContext.Provider value={{ state, dispatch }}>
            <UserContext.Provider value={{ user, setUser }}>
              <div className="App container">
                <BrowserRouter>
                  <Routes>
                    <Route exact path="/" element={<Main />} />
                    <Route path="/sa" element={<SuperAdminLogin />} />
                    <Route path="/register" element={<Registration />} />
                    <Route path="/auth/:token" element={<ValidateAuth />} />
                    <Route path="/logout" element={<Logout />} />
                    <Route path="/invalid" element={<Invalid />} />
                    <Route path="/dashboard/*"
                      element={
                        <RequireAuth>
                          <Dashboard />
                        </RequireAuth>
                      } />
                    <Route path="/admin/*"
                      element={
                        <RequireAuth>
                          <Admin />
                        </RequireAuth>
                      } />
                  </Routes>
                </BrowserRouter>
              </div>
            </UserContext.Provider>
          </EntitiesContext.Provider>
        </ApiContext.Provider>
      </AuthContext.Provider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

function RequireAuth({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const api = useContext(ApiContext);
  const user = useContext(UserContext);
  const result = useHydrateAuth();

  if (!result && user.user && !user.user.authenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (result && user.user.id) {
    // add authorization token header
    api.defaults.headers.common['Authorization'] = `Bearer ${result.data.data.id}`;
    // add interceptor to route all unauthorized resposes to login
    api.interceptors.response.use(function (response) {
      return response;
    }, function (error) {
      if (error.response.status === 403) {
        navigate('/invalid');
      }
      return Promise.reject(error);
    });

    return children;
  }
}

export default App;
